import { StatusChip } from "$components/chip";
import { styled, Typography } from "@material-ui/core";
import type { FC } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import type { StudyWorksheet } from "./use-study-worksheets";

const worksheetMessages = defineMessages({
  allNormal: {
    id: "imaging-study-card.worksheet.allNormal",
    defaultMessage: "All normal",
  },
});

const Worksheet = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  paddingBottom: theme.spacing(1.5),
}));

const WorksheetHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const WorksheetTitle = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 700,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
}));

const FieldRow = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.5),
  fontSize: 14,
}));

const FieldLabel = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontFamily: theme.typography.fontRegular,
}));

const FieldValue = styled("span")(({ theme }) => ({
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontBold,
  fontWeight: 700,
}));

const WorksheetFields: FC<{ worksheet: StudyWorksheet }> = ({ worksheet }) => {
  const { formatMessage } = useIntl();

  return (
    <Worksheet>
      <WorksheetHeader>
        <WorksheetTitle>{worksheet.examTypeLabel}</WorksheetTitle>
        {worksheet.allNormal && (
          <StatusChip
            status="valid"
            label={formatMessage(worksheetMessages.allNormal)}
          />
        )}
      </WorksheetHeader>
      {worksheet.indication && (
        <FieldRow>
          <FieldLabel>
            <FormattedMessage
              id="imaging-study-card.worksheet.indication"
              defaultMessage="Indication:"
            />
          </FieldLabel>
          <FieldValue>{worksheet.indication}</FieldValue>
        </FieldRow>
      )}
      {worksheet.notes && (
        <FieldRow>
          <FieldLabel>
            <FormattedMessage
              id="imaging-study-card.worksheet.notes"
              defaultMessage="Notes:"
            />
          </FieldLabel>
          <FieldValue>{worksheet.notes}</FieldValue>
        </FieldRow>
      )}
    </Worksheet>
  );
};

export const StudyWorksheetDetails: FC<{
  worksheets: StudyWorksheet[];
}> = ({ worksheets }) => (
  <>
    {worksheets.map((worksheet) => (
      <WorksheetFields key={worksheet.id} worksheet={worksheet} />
    ))}
  </>
);
